import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import { toToon, addCharacterOutline } from "./toon";
import { addXraySilhouette } from "./xray-silhouette";

export const TINT_OWN   = new THREE.Color(0xf6c25b);  // yellow (existing OWN_TINT)
export const TINT_ALLY  = new THREE.Color(0x4caf50);  // green
export const TINT_ENEMY = new THREE.Color(0xe53935);  // red
export const TINT_DUMMY = new THREE.Color(0x9e9e9e);  // gray (debug spawn-dummies, teamId=99)
export const TINT_DEAD  = new THREE.Color(0x444444);  // dark gray (overlay when state="dead")

// Quaternius Viking source meshes ship with a near-black "Skin" material —
// override at render time to a warm-pale Nordic tone so characters read as
// human. Other source-material names ("Main", "Pants", "Boots", "Hair",
// "Beard", etc.) keep their authored colors except "Main" (team-tinted).
const SKIN_COLOR = new THREE.Color(0xe0b48a);

const CROSSFADE_SEC = 0.18;
// World convention: 1 unit = 1 meter (256u map ≈ 256m wide). Player avatar is the
// RedWorks prehistoric Caveman (Mixamo rig), native bbox ~1.97u tall → 0.9 gives a
// ~1.8m visible character, matching the prior Quaternius Viking (3.6u native × 0.5).
const PLAYER_SCALE = 0.9;

export type PlayerAction = "idle" | "walking" | "running" | "attacking" | "gathering" | "dead";

// Targeting/click proxy. The character mesh is a thin humanoid (~0.5u wide,
// ~1.8u tall) with gaps between the limbs — raycasting it directly makes units
// very hard to click on a zoomed-out MOBA camera (playtest 2026-06-30). We add
// an invisible vertical cylinder, enclosing the body, that the cursor raycast
// hits instead. Sized in WORLD units (divided by PLAYER_SCALE for the scaled
// root): radius ≈ collision footprint (server UNIT_RADIUS 0.6u) + margin so the
// hit area is gap-free and ~3× the silhouette. Tune PICK_* to taste.
const PICK_RADIUS_WORLD = 0.85; // 1.7u clickable diameter (vs ~0.5u visual width)
const PICK_HEIGHT_WORLD = 2.0;  // feet → just above head

export interface Player {
  object: THREE.Object3D;
  /** Invisible cylinder for cursor/right-click picking — see PICK_* above. */
  pickProxy: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  setAction(action: PlayerAction): void;
  getAction(): PlayerAction;
  setRotation(angleY: number): void;
  setTint(color: THREE.Color): void;
  setOpacity(alpha: number): void;
  dispose(): void;
}

export function createPlayer(gltf: GLTF, initialTint: THREE.Color): Player {
  const root = cloneSkeleton(gltf.scene);
  root.scale.setScalar(PLAYER_SCALE);

  // Convert every mesh's source MeshStandardMaterial to MeshToonMaterial so the
  // character cel-shades like the rest of the world (doodads, buildings). The
  // "Main" torso material additionally gets cloned so per-instance team tinting
  // can mutate `color` without sharing across players.
  const tintedMaterials: THREE.MeshToonMaterial[] = [];
  const allToon: THREE.MeshToonMaterial[] = [];
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // Caveman FBX can carry multi-material meshes — map over each material.
    const srcs = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const outMats = srcs.map((src) => {
      if (!src) return src as unknown as THREE.MeshToonMaterial;
      const toon = toToon(src, { heroLighting: true });
      allToon.push(toon);
      const srcName = (src as THREE.MeshStandardMaterial).name ?? "";
      if (srcName === "Main") {
        toon.color = initialTint.clone();
        tintedMaterials.push(toon);
      } else if (/^skin$/i.test(srcName)) {
        toon.color = SKIN_COLOR.clone();
      }
      return toon;
    });
    mesh.material = Array.isArray(mesh.material) ? outMats : outMats[0]!;
  });
  // Team tint targets. Quaternius Viking has a dedicated "Main" torso material to
  // tint. The prehistoric Caveman is a single textured (atlas) material with no
  // "Main" — so fall back to tinting EVERY material. setTint() below is map-aware:
  // textured materials get a gentle wash (texture survives), solid ones a full tint.
  const tintTargets = tintedMaterials.length > 0 ? tintedMaterials : allToon;
  for (const m of tintTargets) m.userData.__baseColor = m.color.clone();
  const applyTint = (color: THREE.Color) => {
    for (const m of tintTargets) {
      const base = (m.userData.__baseColor as THREE.Color | undefined) ?? new THREE.Color(0xffffff);
      if (m.map) m.color.copy(base).lerp(color, 0.5);  // textured: preserve detail
      else m.color.copy(color);                         // solid: full team color
    }
  };
  applyTint(initialTint);

  // Inverted-hull outline pass — must run AFTER the material conversion so it
  // walks the skinned meshes in their final state. Outline material is shared
  // with the skeleton instance for free animation.
  // World-space outline thickness (addCharacterOutline divides out PLAYER_SCALE
  // internally). 0.045 preserves the prior rendered look (was 0.09 object-space
  // × 0.5 scale).
  addCharacterOutline(root, 0.045);

  // Through-geometry x-ray silhouette (feature: trees/buildings occluding
  // OTHER units, not just the local hero-porthole cutout). Tinted by the same
  // relation color the team-tint system uses, so an ally/enemy silhouette
  // seen through a tree reads exactly like the character does when visible.
  const xray = addXraySilhouette(root, initialTint);

  // Invisible pick proxy (see PICK_* constants). Child of root, so it inherits the
  // player's world position; root.scale (PLAYER_SCALE) also scales it, so author the
  // geometry in LOCAL units (world ÷ scale). Cylinder is rotation-invariant about Y,
  // so setRotation() doesn't distort it. visible=false → never rendered, still
  // raycastable in three.js. Root origin is at the feet, so lift the cylinder centre
  // by half its height to span feet→head.
  const rLocal = PICK_RADIUS_WORLD / PLAYER_SCALE;
  const hLocal = PICK_HEIGHT_WORLD / PLAYER_SCALE;
  const pickProxy = new THREE.Mesh(
    new THREE.CylinderGeometry(rLocal, rLocal, hLocal, 10),
    new THREE.MeshBasicMaterial(),
  );
  pickProxy.position.y = hLocal / 2;
  pickProxy.visible = false;
  pickProxy.castShadow = false;
  pickProxy.receiveShadow = false;
  root.add(pickProxy);

  const mixer = new THREE.AnimationMixer(root);
  const idleClip   = THREE.AnimationClip.findByName(gltf.animations, "Idle");
  const walkClip   = THREE.AnimationClip.findByName(gltf.animations, "Walk");
  // Full-speed locomotion clip (V-key off). Fall back to Walk if not present.
  const runClip    = THREE.AnimationClip.findByName(gltf.animations, "Run") ?? null;
  // Quaternius Viking_Male typical names: try several common attack clip names.
  const attackClip =
    THREE.AnimationClip.findByName(gltf.animations, "Sword_Slash") ??
    THREE.AnimationClip.findByName(gltf.animations, "Attack") ??
    THREE.AnimationClip.findByName(gltf.animations, "Punch");
  // Death clip optional; if missing, last frame of Idle is used as freeze.
  const deathClip  = THREE.AnimationClip.findByName(gltf.animations, "Death");
  // Gathering (chop/mine) loop. No bespoke Mine/Chop clip on the Viking model, so
  // we reuse the attack swing — but CLONED so clipAction returns a distinct, freely
  // re-looped action (clipAction caches by clip identity; sharing the attack clip
  // would couple gather's LoopRepeat onto the LoopOnce attack action).
  const gatherSrc =
    THREE.AnimationClip.findByName(gltf.animations, "Mine") ??
    THREE.AnimationClip.findByName(gltf.animations, "Chop") ??
    THREE.AnimationClip.findByName(gltf.animations, "Gather");
  let gatherClip = gatherSrc ?? null;
  if (!gatherClip && attackClip) {
    gatherClip = attackClip.clone();
    gatherClip.name = "GatherSwing";
  }

  if (!idleClip || !walkClip) {
    throw new Error("Viking_Male.gltf missing Idle or Walk animation clip");
  }

  const idle   = mixer.clipAction(idleClip);
  const walk   = mixer.clipAction(walkClip);
  const run    = runClip ? mixer.clipAction(runClip) : null;
  const attack = attackClip ? mixer.clipAction(attackClip) : null;
  const gather = gatherClip ? mixer.clipAction(gatherClip) : null;
  const death  = deathClip  ? mixer.clipAction(deathClip)  : null;
  if (attack) {
    attack.setLoop(THREE.LoopOnce, 1);
    attack.clampWhenFinished = true;
  }
  if (gather) {
    // Repeating swing while harvesting. Period locked to the server gather
    // cadence (SWING_MS = 1000ms for trees/deposits) so one visible swing maps
    // to one chop SFX — the caveman's Mixamo swing clip otherwise loops faster
    // than the impact sound and reads as desynced.
    gather.setLoop(THREE.LoopRepeat, Infinity);
    if (gatherClip) gather.timeScale = gatherClip.duration / 1.0;
  }
  if (death) {
    death.setLoop(THREE.LoopOnce, 1);
    death.clampWhenFinished = true;
  }
  idle.play();

  let current: PlayerAction = "idle";

  return {
    object: root,
    pickProxy,
    mixer,
    setAction(action) {
      if (action === current) return;
      const prev = current;
      current = action;

      const fade = (from: THREE.AnimationAction | null, to: THREE.AnimationAction | null) => {
        if (to) {
          to.reset().play();
          if (from) to.crossFadeFrom(from, CROSSFADE_SEC, true);
        } else if (from) {
          from.fadeOut(CROSSFADE_SEC);
        }
      };

      const pick = (a: PlayerAction): THREE.AnimationAction | null =>
        a === "idle" ? idle
        : a === "walking" ? walk
        : a === "running" ? (run ?? walk)   // fall back to walk if Run clip absent
        : a === "attacking" ? attack
        : a === "gathering" ? gather
        : a === "dead" ? death
        : idle;
      const fromAction = pick(prev);
      const toAction   = pick(action);
      fade(fromAction, toAction ?? idle);

      // Attack: scale clip duration to 1.5s so damage point at 0.25s of clip = 0.25s real time.
      if (action === "attacking" && attack && attackClip) {
        attack.timeScale = attackClip.duration / 1.5;
      }
    },
    getAction() {
      return current;
    },
    setRotation(angleY) {
      root.rotation.y = angleY;
    },
    setTint(color) {
      applyTint(color);
      xray.setTint(color);
    },
    // Task 7: stealth translucency — sets opacity on all mesh materials.
    setOpacity(alpha) {
      for (const mat of allToon) {
        mat.transparent = alpha < 1.0;
        mat.opacity = alpha;
      }
    },
    dispose() {
      mixer.stopAllAction();
      mixer.uncacheRoot(root);
      for (const mat of allToon) mat.dispose();
      pickProxy.geometry.dispose();
      (pickProxy.material as THREE.Material).dispose();
      xray.dispose();
      root.removeFromParent();
    },
  };
}

export function tintFor(self: { teamId: number; sessionId: string }, other: { teamId: number; sessionId: string }, isOwn: boolean): THREE.Color {
  if (isOwn) return TINT_OWN;
  if (other.teamId === 99) return TINT_DUMMY;
  if (other.teamId === self.teamId) return TINT_ALLY;
  return TINT_ENEMY;
}
